// Tour content registry — single source of truth for guided tours.
//
// Step shape:
//   {
//     target:       CSS selector (e.g. '[data-tour="dashboard.welcome"]') or 'body' for a centered modal step.
//     content:      string | React node — body of the popover.
//     title?:       short header.
//     placement?:   'top' | 'bottom' | 'left' | 'right' (+ '-start'/'-end' variants) | 'center' | 'auto'.
//                   Use 'center' with target: 'body' for a modal-style splash step.
//     skipBeacon?:  boolean — true to skip the beacon dot and open the tooltip directly.
//     navigateTo?:  string — route to navigate to BEFORE this step renders (cross-page tours).
//     waitFor?:     CSS selector to wait for after navigation; defaults to `target`.
//   }
//
// To add a new tour:
//   1. Add an entry below keyed by route slug or "getting-started".
//   2. Add data-tour="<key>.<element>" on the corresponding DOM elements.
//   3. (Optional) wire a HelpButton menu entry — happens automatically for any
//      registry key matching a route-slug-keyed tour.

export const GETTING_STARTED_KEY = "getting-started";

export const TOURS = {
  [GETTING_STARTED_KEY]: {
    title: "Get started",
    // onComplete: 'persist' marks tutorial_completed_at on the User when the
    // tour reaches its last step (or is skipped — see TourEngine.js).
    onComplete: "persist",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Welcome",
        content:
          "We'll show you the core pieces of the portal in under 60 seconds. You can dismiss this anytime and re-open it from the help button (bottom right).",
        skipBeacon: true,
      },
      {
        target: '[data-tour="sidebar.build"]',
        title: "Build",
        content:
          "This group is where you create assistants, attach knowledge to them, and embed them anywhere.",
        placement: "right",
      },
      {
        target: '[data-tour="sidebar.assistants"]',
        title: "Assistants",
        content:
          "An assistant is one configured AI agent — its prompt, model, voice, and tools. Let's go there.",
        placement: "right",
        navigateTo: "/ai-agents/assistants",
      },
      {
        target: '[data-tour="assistants.create"]',
        title: "Create your first assistant",
        content:
          "Click here to spin up a new assistant. You can come back and tweak anything later.",
        placement: "bottom",
        waitFor: '[data-tour="assistants.create"]',
      },
      {
        target: "body",
        placement: "center",
        title: "Data Sources",
        content:
          "Upload PDFs, docs, or paste URLs. Your assistants answer from this knowledge using RAG.",
        navigateTo: "/ai-agents/data-sources",
        skipBeacon: true,
      },
      {
        target: "body",
        placement: "center",
        title: "Widgets",
        content:
          "Widgets are the embeddable surfaces for your assistants — a chat bubble, a voice widget, a WhatsApp connector.",
        navigateTo: "/ai-agents/widgets",
        skipBeacon: true,
      },
      {
        target: "body",
        placement: "center",
        title: "Playground",
        content:
          "Try your assistant inline before you ship it. Great for iterating on the system prompt.",
        navigateTo: "/playground",
        skipBeacon: true,
      },
      {
        target: "body",
        placement: "center",
        title: "Conversations",
        content:
          "Every chat with every assistant lands here — transcripts, captured forms, intents, the whole audit trail.",
        navigateTo: "/engagements",
        skipBeacon: true,
      },
      {
        target: '[data-tour="layout.help"]',
        title: "Need a refresher?",
        content:
          "Click this anytime to re-open this tour, or to get a quick walkthrough of the page you're on.",
        placement: "left",
      },
    ],
  },

  dashboard: {
    title: "Dashboard",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Your dashboard",
        content:
          "This is your home base — usage at a glance, recent activity, and shortcuts to the things you build the most.",
        skipBeacon: true,
      },
    ],
  },

  assistants: {
    title: "Assistants",
    steps: [
      {
        target: '[data-tour="assistants.create"]',
        title: "Create an assistant",
        content: "Each assistant has its own prompt, model, voice, and tools.",
        placement: "bottom",
        skipBeacon: true,
      },
    ],
  },

  "data-sources": {
    title: "Data Sources",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Data sources",
        content:
          "Anything you upload here can be attached to an assistant — that's how the assistant answers from your content.",
        skipBeacon: true,
      },
    ],
  },

  widgets: {
    title: "Widgets",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Widgets",
        content:
          "Widgets are how an assistant is exposed to users — a chat bubble on your site, a phone number, WhatsApp, etc.",
        skipBeacon: true,
      },
    ],
  },

  playground: {
    title: "Playground",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Playground",
        content:
          "Quickly chat with any assistant without setting up a widget — great for prompt iteration.",
        skipBeacon: true,
      },
    ],
  },

  conversations: {
    title: "Conversations",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Conversations",
        content:
          "Every chat across every channel lands here. Open any row to see the full transcript and metadata.",
        skipBeacon: true,
      },
    ],
  },

  contacts: {
    title: "Contacts",
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Contacts",
        content:
          "People who have talked to one of your assistants show up here, with their channel handles and custom fields.",
        skipBeacon: true,
      },
    ],
  },

  // wizard: deferred — /build/from-template is hidden today. Markers exist on
  // the wizard components so this becomes a content-only edit when surfaced.
  // wizard: { title: "Build from Template", steps: [...] },
};

// Map a window.location.pathname to the registry key it should surface via HelpButton.
// Returns null if no mini-tour is registered for the current route.
export function getCurrentTourKey(pathname) {
  if (!pathname) return null;
  const p = pathname.toLowerCase();
  if (p === "/" || p.startsWith("/dashboard")) return "dashboard";
  // Data sources is more specific, must match before /ai-agents/assistants.
  if (p.startsWith("/ai-agents/data-sources")) return "data-sources";
  if (p.startsWith("/ai-agents/widgets")) return "widgets";
  if (p.startsWith("/ai-agents/assistants")) return "assistants";
  if (p.startsWith("/playground")) return "playground";
  if (p.startsWith("/engagements")) return "conversations";
  if (p.startsWith("/contacts")) return "contacts";
  return null;
}
